/**
 * Email templates — 10 transactional templates registered
 * with the template renderer.
 *
 * Each template produces both HTML and plain-text output,
 * supports brand configuration, and is responsive.
 */

import type { BrandConfig, EmailContext, RenderedTemplate, EmailTemplateName } from './types';
import {
  registerTemplate,
  interpolate,
  formatDate,
  renderButton,
  renderButtonText,
} from './renderer';
import {
  emailShell,
  textFooter,
  heading,
  paragraph,
  infoBox,
} from './layout';

type TemplateDef = {
  subject: string;
  buildHtml: (ctx: EmailContext, brand: BrandConfig) => string;
  buildText: (ctx: EmailContext, brand: BrandConfig) => string;
};

const TEMPLATES: Record<EmailTemplateName, TemplateDef> = {
  welcome: {
    subject: 'Welcome to {{brandName}}',
    buildHtml: (ctx, brand) => {
      const name = ctx.name as string;
      const verifyUrl = ctx.verifyUrl as string | undefined;
      const body = `
        ${heading(`Welcome, ${name}!`, brand.primaryColor)}
        ${paragraph(`Thanks for joining ${brand.name}. We're excited to have you as part of our creator community.`)}
        ${paragraph('To get started, please verify your email address to unlock all features.')}
        ${verifyUrl ? renderButton('Verify Email', verifyUrl, brand.primaryColor) : ''}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const name = ctx.name as string;
      const verifyUrl = ctx.verifyUrl as string | undefined;
      let text = `Welcome, ${name}!\n\nThanks for joining ${brand.name}. We're excited to have you as part of our creator community.\n\nTo get started, please verify your email address to unlock all features.`;
      if (verifyUrl) text += `\n\n${renderButtonText('Verify Email', verifyUrl)}`;
      text += textFooter(brand);
      return text;
    },
  },

  verify_email: {
    subject: 'Verify your email address',
    buildHtml: (ctx, brand) => {
      const name = ctx.name as string;
      const verifyUrl = ctx.verifyUrl as string;
      const body = `
        ${heading('Verify Your Email', brand.primaryColor)}
        ${paragraph(`Hi ${name}, please confirm your email address to complete your account setup.`)}
        ${infoBox('This link expires in 24 hours for security.', brand.primaryColor)}
        ${renderButton('Verify Email', verifyUrl, brand.primaryColor)}
        ${paragraph(`If the button doesn't work, copy and paste this link: <a href="${verifyUrl}" style="color:${brand.primaryColor};">${verifyUrl}</a>`)}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const name = ctx.name as string;
      const verifyUrl = ctx.verifyUrl as string;
      return `Verify Your Email\n\nHi ${name}, please confirm your email address to complete your account setup.\n\nThis link expires in 24 hours for security.\n\n${renderButtonText('Verify Email', verifyUrl)}\n\nIf the link doesn't work, copy and paste: ${verifyUrl}${textFooter(brand)}`;
    },
  },

  password_reset: {
    subject: 'Reset your password',
    buildHtml: (ctx, brand) => {
      const name = ctx.name as string;
      const resetUrl = ctx.resetUrl as string;
      const body = `
        ${heading('Reset Your Password', brand.primaryColor)}
        ${paragraph(`Hi ${name}, we received a request to reset your password.`)}
        ${renderButton('Reset Password', resetUrl, brand.primaryColor)}
        ${paragraph(`If the button doesn't work, copy and paste this link: <a href="${resetUrl}" style="color:${brand.primaryColor};">${resetUrl}</a>`)}
        ${infoBox("If you didn't request this, you can safely ignore this email.", '#94a3b8')}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const name = ctx.name as string;
      const resetUrl = ctx.resetUrl as string;
      return `Reset Your Password\n\nHi ${name}, we received a request to reset your password.\n\n${renderButtonText('Reset Password', resetUrl)}\n\nIf the link doesn't work, copy and paste: ${resetUrl}\n\nIf you didn't request this, you can safely ignore this email.${textFooter(brand)}`;
    },
  },

  application_submitted: {
    subject: 'Your application has been submitted',
    buildHtml: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      const submittedAt = formatDate(ctx.submittedAt as string);
      const body = `
        ${heading('Application Submitted', brand.primaryColor)}
        ${paragraph(`Hi ${name}, your creator application has been successfully submitted.`)}
        ${infoBox(`Application ID: ${applicationId}<br/>Submitted on: ${submittedAt}`, brand.primaryColor)}
        ${paragraph('Our review team will evaluate your application. You\'ll receive an email update when the status changes.')}
        ${paragraph(`Track your application status in your <a href="${brand.applicationUrl}/workspace/status" style="color:${brand.primaryColor};">workspace</a>.`)}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      const submittedAt = formatDate(ctx.submittedAt as string);
      return `Application Submitted\n\nHi ${name}, your creator application has been successfully submitted.\n\nApplication ID: ${applicationId}\nSubmitted on: ${submittedAt}\n\nOur review team will evaluate your application. You'll receive an email update when the status changes.\n\nTrack your status: ${brand.applicationUrl}/workspace/status${textFooter(brand)}`;
    },
  },

  application_under_review: {
    subject: 'Your application is now under review',
    buildHtml: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      const body = `
        ${heading('Application Under Review', brand.primaryColor)}
        ${paragraph(`Hi ${name}, your application is now being reviewed by our team.`)}
        ${infoBox(`Application ID: ${applicationId}`, brand.primaryColor)}
        ${paragraph('This process typically takes a few business days. We\'ll notify you once a decision is made.')}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      return `Application Under Review\n\nHi ${name}, your application is now being reviewed by our team.\n\nApplication ID: ${applicationId}\n\nThis process typically takes a few business days. We'll notify you once a decision is made.${textFooter(brand)}`;
    },
  },

  application_approved: {
    subject: 'Congratulations! Your application is approved',
    buildHtml: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      const approvedAt = formatDate(ctx.approvedAt as string);
      const body = `
        ${heading('Application Approved!', '#059669')}
        ${paragraph(`Hi ${name}, congratulations! Your creator application has been approved.`)}
        ${infoBox(`Application ID: ${applicationId}<br/>Approved on: ${approvedAt}`, '#059669')}
        ${paragraph('You now have full access to UNIVIBE. Welcome aboard!')}
        ${renderButton('Go to Dashboard', `${brand.applicationUrl}/dashboard`, brand.primaryColor)}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      const approvedAt = formatDate(ctx.approvedAt as string);
      return `Application Approved!\n\nHi ${name}, congratulations! Your creator application has been approved.\n\nApplication ID: ${applicationId}\nApproved on: ${approvedAt}\n\nYou now have full access to UNIVIBE. Welcome aboard!\n\n${renderButtonText('Go to Dashboard', `${brand.applicationUrl}/dashboard`)}${textFooter(brand)}`;
    },
  },

  application_rejected: {
    subject: 'Update on your application',
    buildHtml: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      const reason = ctx.reason as string | undefined;
      const body = `
        ${heading('Application Update', '#dc2626')}
        ${paragraph(`Hi ${name}, unfortunately your creator application was not approved at this time.`)}
        ${infoBox(`Application ID: ${applicationId}`, '#dc2626')}
        ${reason ? paragraph(`<strong>Reason:</strong> ${reason}`) : ''}
        ${paragraph('You may update your profile and submit a new application in the future.')}
        ${paragraph(`If you have questions, contact <a href="mailto:${brand.supportEmail}" style="color:${brand.primaryColor};">${brand.supportEmail}</a>.`)}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const name = ctx.name as string;
      const applicationId = ctx.applicationId as string;
      const reason = ctx.reason as string | undefined;
      let text = `Application Update\n\nHi ${name}, unfortunately your creator application was not approved at this time.\n\nApplication ID: ${applicationId}`;
      if (reason) text += `\n\nReason: ${reason}`;
      text += `\n\nYou may update your profile and submit a new application in the future.\n\nIf you have questions, contact ${brand.supportEmail}.${textFooter(brand)}`;
      return text;
    },
  },

  admin_notification: {
    subject: '{{subject}}',
    buildHtml: (ctx, brand) => {
      const body = `
        ${heading(interpolate('{{subject}}', ctx), brand.primaryColor)}
        ${paragraph(interpolate('{{message}}', ctx))}
        ${ctx.actionUrl ? renderButton(interpolate('{{actionLabel}}', ctx) || 'View', ctx.actionUrl as string, brand.primaryColor) : ''}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      let text = `${interpolate('{{subject}}', ctx)}\n\n${interpolate('{{message}}', ctx)}`;
      if (ctx.actionUrl) {
        text += `\n\n${renderButtonText(interpolate('{{actionLabel}}', ctx) || 'View', ctx.actionUrl as string)}`;
      }
      text += textFooter(brand);
      return text;
    },
  },

  review_assigned: {
    subject: 'New application assigned for review',
    buildHtml: (ctx, brand) => {
      const reviewerName = ctx.reviewerName as string;
      const applicantName = ctx.applicantName as string;
      const applicationId = ctx.applicationId as string;
      const reviewUrl = ctx.reviewUrl as string;
      const body = `
        ${heading('Review Assignment', brand.primaryColor)}
        ${paragraph(`Hi ${reviewerName}, a new application has been assigned to you for review.`)}
        ${infoBox(`Applicant: ${applicantName}<br/>Application ID: ${applicationId}`, brand.primaryColor)}
        ${renderButton('Review Application', reviewUrl, brand.primaryColor)}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      const reviewerName = ctx.reviewerName as string;
      const applicantName = ctx.applicantName as string;
      const applicationId = ctx.applicationId as string;
      const reviewUrl = ctx.reviewUrl as string;
      return `Review Assignment\n\nHi ${reviewerName}, a new application has been assigned to you for review.\n\nApplicant: ${applicantName}\nApplication ID: ${applicationId}\n\n${renderButtonText('Review Application', reviewUrl)}${textFooter(brand)}`;
    },
  },

  generic_notification: {
    subject: '{{subject}}',
    buildHtml: (ctx, brand) => {
      const body = `
        ${heading(interpolate('{{subject}}', ctx), brand.primaryColor)}
        ${paragraph(interpolate('{{message}}', ctx))}
        ${ctx.actionUrl ? renderButton(interpolate('{{actionLabel}}', ctx) || 'Learn More', ctx.actionUrl as string, brand.primaryColor) : ''}
      `;
      return emailShell(body, brand);
    },
    buildText: (ctx, brand) => {
      let text = `${interpolate('{{subject}}', ctx)}\n\n${interpolate('{{message}}', ctx)}`;
      if (ctx.actionUrl) {
        text += `\n\n${renderButtonText(interpolate('{{actionLabel}}', ctx) || 'Learn More', ctx.actionUrl as string)}`;
      }
      text += textFooter(brand);
      return text;
    },
  },
};

export function registerAllTemplates(): void {
  for (const [name, def] of Object.entries(TEMPLATES)) {
    registerTemplate(name, (ctx: EmailContext, brand: BrandConfig): RenderedTemplate => {
      const subject = interpolate(def.subject, { ...ctx, brandName: brand.name });
      return {
        subject,
        htmlBody: def.buildHtml(ctx, brand),
        textBody: def.buildText(ctx, brand),
      };
    });
  }
}

export { TEMPLATES };
